import { t } from "i18next";

import BaseLabel from "components/BaseLabel";
import SearchEmpty from "components/SearchEmpty";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";

import {
  ChangeLogData,
  GroupedVersion,
  ReleaseNote,
  Version,
} from "./interfaces";
import ScrollableContainer from "./styles";
import { colors } from "../../../global/globalStyle";
import { changeLogService } from "../../../services/changeLog";

export const groupByVersionNumber = (versions: Version[]): GroupedVersion[] => {
  return versions.reduce((acc: GroupedVersion[], current: Version) => {
    const existingVersion = acc.find(
      (v) => v.VersionNumber === current.versionNumber,
    );

    if (existingVersion) {
      existingVersion.ReleaseNotes.push(...current.releaseNotes);
    } else {
      acc.push({
        VersionNumber: current.versionNumber,
        Description: current.description,
        ReleaseDate: current.releaseDate,
        ReleaseNotes: [...current.releaseNotes],
      });
    }

    return acc;
  }, []);
};

export const groupVersions = (data: ChangeLogData) => {
  const { productVersionsBase, productVersionsCustomizations } = data.data;

  const groupedBase = productVersionsBase.map((version) => ({
    versionNumber: version.versionNumber,
    description: version.description,
    releaseDate: version.releaseDate,
    releaseNotes: version.releaseNotes,
  }));

  const groupedCustomizations = productVersionsCustomizations.map(
    (version) => ({
      versionNumber: version.versionNumber,
      description: version.description,
      releaseDate: version.releaseDate,
      releaseNotes: version.releaseNotes,
    }),
  );

  const returnChangeLogData: ChangeLogData = {
    data: {
      productVersionsBase: groupedBase,
      productVersionsCustomizations: groupedCustomizations,
    },
  };
  return returnChangeLogData;
};

export const getAllChangeLogs = async () => {
  const response = await changeLogService.getAll();
  return response?.data;
};

const renderAccordionList = (versions: Version[]) =>
  versions.map((version: Version) => (
    <Accordion
      key={version.versionNumber}
      disableGutters
      sx={{
        marginBottom: "8px",
        border: `1px solid ${colors.neutral_gray_90}`,
        borderRadius: "6px",
        backgroundColor: colors.neutral_gray_90,
        boxShadow: "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        aria-controls={`${version.versionNumber}-content-batata`}
        id={`${version.versionNumber}-header`}
        sx={{ padding: "8px 14px" }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap="2px"
          width="100%"
          // alignItems="center"
          sx={{ margin: "0"}}
        >
          <BaseLabel
            children={version.versionNumber}
            fontWeight="700"
            fontSize="16px"
          />
          <BaseLabel
            children={new Date(version.releaseDate).toLocaleDateString()}
            fontWeight="600"
            fontSize="12px"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "0 14px 20px 14px" }}>
        <BaseLabel
          children={version.description}
          fontWeight="600"
          fontSize="13px"
        />
        <ul style={{ paddingLeft: "16px", marginTop: "6px" }}>
          {version.releaseNotes.map((note: ReleaseNote, index: number) => (
            <li key={index}>
              <BaseLabel children={note.noteDescription} fontSize="13px" />
            </li>
          ))}
        </ul>
      </AccordionDetails>
    </Accordion>
  ));

export const handleChangeLog = (
  changeLogData: ChangeLogData,
  productVersionsBase: Version[],
  productVersionsCustomizations: Version[],
) => {
  const isChangeLogValid =
    changeLogData !== undefined && changeLogData !== null;
  const isBaseValid = productVersionsBase && productVersionsBase.length > 0;
  const isCustomValid =
    productVersionsCustomizations && productVersionsCustomizations.length > 0;

  if (!isChangeLogValid && !isBaseValid && !isCustomValid) {
    return (
      <SearchEmpty
        textMessage={t("changeLog.noVersionAvailable")}
        typeIcon={"file"}
      />
    );
  }

  return (
    <ScrollableContainer>
      {isBaseValid ? (
        <div>
          <h3
            style={{
              fontWeight: "bold",
              fontSize: "18px",
              marginBottom: "8px",
            }}
          >
            {t("changeLog.baseVersions")}
          </h3>
          {renderAccordionList(productVersionsBase)}
        </div>
      ) : null}

      {isCustomValid ? (
        <div>
          <h3
            style={{
              fontWeight: "bold",
              fontSize: "18px",
              marginBottom: "16px",
              marginTop: "16px",
            }}
          >
            {t("changeLog.customVersions")}
          </h3>
          {renderAccordionList(productVersionsCustomizations)}
        </div>
      ) : null}

      {!isBaseValid && !isCustomValid ? (
        <SearchEmpty
          textMessage={t("changeLog.noVersionAvailable")}
          typeIcon={"file"}
        />
      ) : null}
    </ScrollableContainer>
  );
};

export const handleChangeLogHtml = (
  changeLogData: ChangeLogData,
): JSX.Element => {
  const { productVersionsBase, productVersionsCustomizations } =
    changeLogData.data;

  return handleChangeLog(
    changeLogData,
    productVersionsBase,
    productVersionsCustomizations,
  );
};
